import { ICommunicationItem } from '../Interfaces/ICommunication';

/** Aufgelöste Vorgangs-Metadaten für den Druckkopf (Namen bereits gemappt). */
export interface IEmailPrintMeta {
  title?: string | null;
  /** Interne DiNo-Vorgangsnummer (idLegalTransaction). */
  dinoNumber?: number | null;
  /** Aktenzeichen (TransactionNumberArray). */
  fileNumbers?: string[];
  /** Urkundennummern (RecordNumberArray). */
  recordNumbers?: string[];
  notaryName?: string | null;
  employeeOrGroup?: string | null;
}

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtDateTime = (iso?: string): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('de-DE');
};

const rowsHtml = (rows: Array<[string, string]>): string =>
  rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${v}</td></tr>`).join('');

/**
 * Baut ein eigenständiges, druckfertiges HTML-Dokument für eine E-Mail.
 * Kopfblock mit Vorgangs-Metadaten (sofern vorhanden) + E-Mail-Header + Body
 * + Anhangsliste. Die Werte werden escaped; der HTML-Body einer HTML-Mail wird
 * bewusst unverändert eingebettet (eigenes Druckfenster, kein DiNo-UI-Scope).
 */
export const buildEmailPrintHtml = (
  item: ICommunicationItem,
  meta: IEmailPrintMeta | undefined,
  printedByName: string,
  printedAtIso: string
): string => {
  const directionLabel =
    item.Direction === 'incoming'
      ? 'Eingehend'
      : item.Status === 'sent'
        ? 'Ausgehend (gesendet)'
        : 'Ausgehend (geplant)';

  const metaRows: Array<[string, string]> = [];
  if (meta) {
    if (meta.title) metaRows.push(['Vorgang', esc(meta.title)]);
    if (meta.dinoNumber != null)
      metaRows.push(['DiNo-Nr.', esc(meta.dinoNumber)]);
    if (meta.fileNumbers && meta.fileNumbers.length > 0)
      metaRows.push(['Aktenzeichen', esc(meta.fileNumbers.join(', '))]);
    if (meta.recordNumbers && meta.recordNumbers.length > 0)
      metaRows.push(['Urkundennummer', esc(meta.recordNumbers.join(', '))]);
    if (meta.notaryName) metaRows.push(['Zust. Notar', esc(meta.notaryName)]);
    if (meta.employeeOrGroup)
      metaRows.push(['Zust. Mitarbeiter/Gruppe', esc(meta.employeeOrGroup)]);
  }
  metaRows.push(['Gedruckt von', esc(printedByName || '–')]);
  metaRows.push(['Druckdatum', esc(fmtDateTime(printedAtIso))]);

  const mailRows: Array<[string, string]> = [];
  if (item.From) mailRows.push(['Von', esc(item.From)]);
  if (item.To) mailRows.push(['An', esc(item.To)]);
  if (item.Cc) mailRows.push(['Cc', esc(item.Cc)]);
  mailRows.push(['Datum', esc(fmtDateTime(item.When))]);
  mailRows.push(['Richtung', esc(directionLabel)]);
  mailRows.push(['Betreff', esc(item.Subject || '(ohne Betreff)')]);

  const bodyHtml =
    item.BodyContentType === 'html'
      ? (item.Body ?? '')
      : `<pre style="white-space:pre-wrap;font-family:inherit;margin:0;">${esc(
          item.Body ?? ''
        )}</pre>`;

  const attachments = (item.Attachments ?? [])
    .map((a, i) => `<li>${esc(a.FileName || `Anhang ${i + 1}`)}</li>`)
    .join('');

  return `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8">
<title>${esc(item.Subject || 'E-Mail')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color:#111; margin: 24px; }
  h1 { font-size: 16px; margin: 0 0 12px; }
  table.meta { border-collapse: collapse; width: 100%; margin: 0 0 8px; }
  table.meta th { text-align:left; width: 190px; vertical-align: top; padding: 2px 8px 2px 0; color:#444; font-weight:600; }
  table.meta td { padding: 2px 0; word-break: break-word; }
  hr.sep { border:0; border-top:1px solid #ccc; margin: 12px 0; }
  .body { margin-top: 12px; }
  img { max-width: 100%; height: auto; }
  ul { margin: 4px 0; padding-left: 18px; }
  @media print { body { margin: 0; } }
</style></head><body>
  <h1>E-Mail-Dokumentation</h1>
  <table class="meta">${rowsHtml(metaRows)}</table>
  <hr class="sep">
  <table class="meta">${rowsHtml(mailRows)}</table>
  <hr class="sep">
  <div class="body">${bodyHtml}</div>
  ${
    attachments
      ? `<hr class="sep"><strong>Anhänge:</strong><ul>${attachments}</ul>`
      : ''
  }
</body></html>`;
};
